import React, { useEffect, useState } from "react";
import "./App.css";
import { useSelector, useDispatch } from "react-redux";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import * as ROUTES from "./constants/routes";
import { validateUserSession as validateUserAction } from "./actions/auth_actions";
import { Cache } from "aws-amplify/utils";
import LinearProgress from "@mui/material/LinearProgress";

import config from "./config/";
import { useAbility } from "@casl/react";

import { AbilityContext } from './components/Abilities'
import defineRulesFor from "./config/permissions";
//componenets
import Login from "./components/Auth/Login";
import Forget from "./components/Auth/Forget";
import Main from "./components/Main";
import Maintenance from "./components/Main/Maintenance";
import Print from "./components/Print";
import Help from "./components/Help";
import AutoLogout from './components/AutoLogout';
import PricingTable from './components/Auth/PricingTable';

const NotFoundRedirect = () => <Navigate to="/" />;

const PublicRoute = ({ children }) => {
  const authStatus = useSelector((state) => state.auth.authenticated);
  const [lastPath, setLastPath] = useState(null);

  useEffect(() => {
    const fetchLastPath = async () => {
      try {
        const path = await Cache.getItem("lastpath");
        setLastPath(path || ROUTES.MAIN);
      } catch (error) {
        console.error("Error fetching lastpath from cache:", error);
        setLastPath(ROUTES.MAIN); // Fallback to main route on error
      }
    };

    if (authStatus === true) {
      fetchLastPath();
    }
  }, [authStatus]);

  if (authStatus === null) { // Still validating
    return <LinearProgress />;
  }

  if (authStatus === false) {
    return children; // User is not authenticated, show the public page.
  }

  // User is authenticated, wait for lastPath to be fetched, then navigate.
  return lastPath ? <Navigate to={lastPath} /> : <LinearProgress />;
};

const PrivateRoute = ({ children, init }) => {
  const { authenticated: authStatus } = useSelector((state) => state.auth);
  const ability = useAbility(AbilityContext);
  const location = useLocation();
  const path = location.pathname;

  if (authStatus === null || (authStatus === true && !init)) { // Still validating or initializing permissions
    return <LinearProgress />;
  }

  if (authStatus === false) {
    return <Navigate to={ROUTES.LOGIN} />;
  }
  // Check permission
  if (ability.can("visit", path)) {
    return <><AutoLogout />{children}</>;
  }

  // Redirect to parent path if permission is denied
  const parentPath = path.substring(0, path.lastIndexOf('/')) || '/';
  return <Navigate to={parentPath} />;
};

const DefaultRoute = ({ init }) => {
  const { authenticated: authStatus } = useSelector((state) => state.auth);

  Cache.removeItem("lastpath");

  if (authStatus === null || (authStatus === true && !init)) {
    return <LinearProgress />;
  }

  return authStatus ? <Navigate to={ROUTES.MAIN} /> : <Navigate to={ROUTES.LOGIN} />;
};

function App() {
  const [init, setInit] = useState(false);
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);
  const { authenticated, role } = useSelector((state) => state.auth);
  const portalDetails = useSelector((state) => state.portaldetails);

  // Validate user session periodically
  useEffect(() => {
    const validateUser = () => {
      if (authenticated !== false) {
        dispatch(validateUserAction());
      }
    };

    validateUser(); // Initial validation
    const interval = setInterval(validateUser, config.DEFAULT_SESSIONCHECK_TIME);

    return () => clearInterval(interval);
  }, [authenticated]);

  useEffect(() => {
    if (!role) return;

    if (role === "flexible") {
      if (portalDetails?.user?.viewPermissions) {
        const optional_rules = defineRulesFor(role, portalDetails.user.viewPermissions);
        ability.update(optional_rules);
        setInit(true);
      }
    } else {
      const rules = defineRulesFor(role);
      ability.update(rules);
      if (portalDetails?.user) {
        setInit(true);
      }
    }
  }, [role, portalDetails, ability]);

  const doingMaintnance = config.doingMaintnance || false;

  if (doingMaintnance) {
    return <Maintenance />;
  }

  return (
    <div>
      <Help isAuthenticated={authenticated}/>
      <Routes>
        <Route path="/" element={<DefaultRoute init={init} />} />

        <Route path={ROUTES.PRINT} element={<PrivateRoute init={init}><Print /></PrivateRoute>} />
        <Route path={ROUTES.MAIN} element={<PrivateRoute init={init}><Main /></PrivateRoute>} />
        <Route path={`${ROUTES.MAIN}/*`} element={<PrivateRoute init={init}><Main /></PrivateRoute>} />

        <Route path={ROUTES.LOGIN} element={<PublicRoute><Login /></PublicRoute>} />
        <Route path={ROUTES.FORGET} element={<PublicRoute><Forget /></PublicRoute>} />
        <Route path={ROUTES.PRICING} element={<PublicRoute><PricingTable /></PublicRoute>} />

        <Route path="*" element={<NotFoundRedirect />} />
      </Routes>
    </div>
  );
}

export default App;